import { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";

import { getAccessToken } from "../../services/authApi";
import { assignAttachmentToDocument, createDocument, getAttachmentUploadUrl, uploadAttachmentBlob, getExistingDocumentId } from "../../services/documentApi";
import { convertMismoToLoanObject, createLoan, getLienHolderFieldUpdates, getArmIndex, fieldWriter, addFieldLockData } from "../../services/loanApi";
import { getTPOContact, getUserDetails, getTpoSettingsAllBasicInfo, getSalesRepAssignment, getBrokerLoAeAssignment, getTPOContactByOrg } from "../../services/userApi";

import { PERMISSIONS } from "../../constants/permissions";

import { hasPermission } from "../../utils/authUtils";
import { getPhoenixISODate } from "../../utils/dateUtils";
import { logError } from "../../utils/logError";
import { collectSoftLicenseWarnings, skipStateLicenseValidation } from "../../utils/licenseValidation";
import { info, error as logAppInsightError } from "../../utils/logger";
import {
    getActiveLicenseForState,
    extractCompanyDetails,
    extractUserDetails,
    buildInterviewerName,
    applyStaticImportParityForManual,
    getProgressWidthChannels,
    formatLoanErrorMessage,
    parseErrorModalBlocks,
    addFieldsToDictionary,
    buildFieldWriterPayload,
    updateLateFeeFields,
    updateTPOAEtoLoan,
    applyAEtoLoan,
    addLoCompensationIfAvailable,
    correspondentLockedImportFields,
    getValueByContractPath,
    getTeamCustomerPk,
    clearRentalIncomeWithoutReo,
    getImportedRentalIncomeFieldWrite
} from "../../utils/loanHelper";
import { getLoggedInUserPersonas } from "../../utils/userPersonas";

import Loading from "../layout/Loading";
import FormLoader from "../layout/FormLoader";
import Toast from "../shared/Toast";
import ErrorBlocks from "../shared/ErrorBlocks";

import "../../common.css";
import "./NewLoan.css";

const NewLoanForm = () => {
    const [accessToken, setAccessToken] = useState(null);
    const [uploadedXmlContent, setUploadedXmlContent] = useState("");
    const [loanType, setLoanType] = useState(null);
    const [method, setMethod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState(0);
    const [uploadedFile, setUploadedFile] = useState(null);

    // Permission gating: default allow; only correspondent-only orgs require persona check.
    const [hasCreateLoanAccess, setHasCreateLoanAccess] = useState(true);

    const [loanOfficerList, setLoanOfficerList] = useState([]);
    const [loanProcessorList, setLoanProcessorList] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState("");
    const [selectedLoanOfficer, setSelectedLoanOfficer] = useState("");
    const [selectedLoanProcessor, setSelectedLoanProcessor] = useState("");

    const [tpoCompanies, setTpoCompanies] = useState([]);
    const [tpoLoanOfficers, setTpoLoanOfficers] = useState([]);
    const [tpoLoanProcessors, setTpoLoanProcessors] = useState([]);
    const [tpoParent, setTpoParent] = useState(null);
    const [stateLicenses, setStateLicenses] = useState(null);

    const [errorMessage, setErrorMessage] = useState("");
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [propertyState, setPropertyState] = useState("");
    const [propertyCity, setPropertyCity] = useState("");
    const [fileError, setFileError] = useState("");
    const [tpoUserId, setTpoUserId] = useState(null);
    const [showAEDropdown, setShowAEDropdown] = useState(false);
    const [salesRepAeList, setSalesRepAeList] = useState([]);
    const [selectedAeUserId, setSelectedAeUserId] = useState("");
    const [aeBdrInfo, setAeBdrInfo] = useState(null);

    const [channelType, setChannelType] = useState("");
    // values: "WHOLESALE" | "CORR_NON_DELEGATED" | "CORR_DELEGATED"
    const [showStep0, setShowStep0] = useState(false);
    const [channelOptions, setChannelOptions] = useState([]);

    const [orgOptions, setOrgOptions] = useState([]);
    const [selectedOrgEntityId, setSelectedOrgEntityId] = useState("");
    const [mainOrgCache, setMainOrgCache] = useState(null);
    const [switchingOrg, setSwitchingOrg] = useState(false);
    const [peerLoanAccess, setPeerLoanAccess] = useState("");

    const [createdLoanId, setCreatedLoanId] = useState(() => {
        return sessionStorage.getItem("newLoan.createdLoanId") || "";
    });

    const [createdLoanNumber, setCreatedLoanNumber] = useState(() => {
        return sessionStorage.getItem("newLoan.createdLoanNumber") || "";
    });

    const ENABLE_COMPANY_SWITCH =
        String(process.env.REACT_APP_ENABLE_COMPANY_SWITCH_DROPDOWN || "false").toLowerCase() === "true";
    const ENABLE_AE_BDR_FEATURE =
        String(process.env.REACT_APP_ENABLE_AE_BDR_FEATURE || "false").toLowerCase() === "true";

    const ENABLE_AE_BDR_FOR_CORR =
        String(process.env.REACT_APP_ENABLE_AE_BDR_FOR_CORR || "false").toLowerCase() === "true";
    const correspondentDelegated = process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT_DELEGATED;
    const correspondentNonDelegated = process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT_NON_DELEGATED;
    let correspondentType = correspondentDelegated; // default to delegated if both are present, otherwise will be overridden in submitLoan based on channelType

    const isReadWrite = (v) => String(v || "").trim().toLowerCase() === "readwrite";

    const buildUploadContext = (extra = {}) => ({
        component: "LoanForm",
        function: "FileUpload",
        step,
        method,
        loanType,
        hasUploadedFile: !!uploadedFile,
        uploadedFileName: uploadedFile?.name || null,
        uploadedFileSize: uploadedFile?.size || null,
        uploadedFileType: uploadedFile?.type || null,
        hasXmlContent: !!uploadedXmlContent,
        ...extra
    });

    const [forceBusinessFlow, setForceBusinessFlow] = useState(false); // when nmlsId is empty
    const [hideStep1, setHideStep1] = useState(false);
    const [hideBackInStep2, setHideBackInStep2] = useState(false);

    const clearCreatedLoanSession = () => {
        sessionStorage.removeItem("newLoan.createdLoanId");
        sessionStorage.removeItem("newLoan.createdLoanNumber");
        setCreatedLoanId("");
        setCreatedLoanNumber("");
    };

    useEffect(() => {
        clearCreatedLoanSession();

        info("TPO Add New Page loaded", { component: "LoanForm" });

        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            initializeAuthObject();
        } else {
            console.warn("elli.script.guest is not available.");
            const errorCode = logError("GUEST_NOT_AVAILABLE");
            setErrorMessage(`Error Code: ${errorCode} - Guest functionality is not available.`);
            setShowErrorPopup(true);

            logAppInsightError("window.elli load failed", {
                problemId: errorCode,
                errorCode,
                errorMessage: `${errorCode} - Guest functionality is not available."}`,
                component: "LoanForm"
            });
        }
    }, []);

    const initializeAuthObject = async () => {

        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            try {
                const authCode = await tpoAuthObject.createAuthCode();
                const token = await getAccessToken(authCode);
                if (token) {
                    setAccessToken(token);
                    tpoAuthObject.getUser().then(async function (user) {
                        try {
                            // 1) Call getUserDetails on page load (after auth)
                            const userDetails = await getUserDetails(token, user.id);
                            // Get orgId from userDetails.organization.entityId
                            const orgId = userDetails?.organization?.entityId;

                            // 2) Call TPO settings endpoint using orgId
                            const tpoSettings = await getTpoSettingsAllBasicInfo(token, orgId);

                            // Call the new function with params from company
                            const channelTypes = tpoSettings?.companyDetails?.channels ?? [];



                            // Only correspondent-only orgs need CREATE_LOANS persona check.
                            const isCorrespondent = channelTypes.includes("Correspondent");
                            const isCorrespondentOnly = channelTypes.includes("Correspondent") && !channelTypes.includes("Broker");

                            let hasCreateLoan = true;

                            // apply restrictions for correspondent-only orgs based on personas/permissions
                            if (isCorrespondent) {
                                try {
                                    const personas = await getLoggedInUserPersonas();
                                    hasCreateLoan = hasPermission(personas, PERMISSIONS.CREATE_LOANS);
                                } catch (err) {
                                    console.warn("Failed to evaluate CREATE_LOANS permission:", err);
                                    hasCreateLoan = false;
                                }
                            }

                            if (isCorrespondentOnly) {
                                setHasCreateLoanAccess(hasCreateLoan);
                            } else {
                                setHasCreateLoanAccess(true); // non-correspondent orgs have no restrictions   
                            }

                            const correspondentSelection = tpoSettings?.companyDetails?.underwritingTypes ?? [];
                            // read nmlsId from response
                            const nmlsId =
                                tpoSettings?.basicInfo?.businessInformation?.nmlsId || "";
                            applyChannelDecision({
                                channelTypes,
                                correspondentSelection,
                                hasNmls: Boolean(String(nmlsId).trim()),
                                hasCreateLoan
                            });
                        } catch (e) {
                            console.error("UserDetails/TPO basicInfo check failed:", e);
                            // keep existing flow (do not block user)
                        }
                        finally {
                            setLoading(false);
                        }
                    });

                }
                else {
                    const errorCode = logError("AUTH_BLOCK_ERROR");
                    logAppInsightError("Authentication failed.", {
                        problemId: errorCode,
                        errorCode,
                        errorMessage: `${errorCode} - Authentication failed."}`,
                        component: "LoanForm"
                    });

                    redirectToErrorPage(`Error Code: ${errorCode} - Authentication failed.`);
                }
            } catch (error) {
                console.error("createAuthCode Error:", error);
                const errorCode = logError("AUTH_BLOCK_ERROR");
                setErrorMessage(`Error Code: ${errorCode} - Authentication failed.`);
                setShowErrorPopup(true);
                logAppInsightError("Authentication failed.", {
                    problemId: errorCode,
                    errorCode,
                    errorMessage: `${errorCode} - Authentication failed."}`,
                    component: "LoanForm"
                });
                redirectToErrorPage(`Error Code: ${errorCode} - Authentication failed.`);
            }
        } catch (error) {
            console.error("Auth block Error:", error);
            const errorCode = logError("AUTH_CODE_ERROR");
            setErrorMessage(`Error Code: ${errorCode} - Unable to generate Auth Code.`);
            setShowErrorPopup(true);
            logAppInsightError("Unable to generate Auth Code.", {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Unable to generate Auth Code."}`,
                component: "LoanForm"
            });
            redirectToErrorPage(`Error Code: ${errorCode} - Unable to generate Auth Code.`);
        }
    };

    const redirectToErrorPage = (message) => {
        window.location.href = `/exception?message=${encodeURIComponent(message)}&page=loan`;
    };

    const handleNext = async () => {
        setSubmitting(true);
        let state = "";
        setPropertyState("");
        setPropertyCity("");

        if (method === "import") {

            // 1. NEXT clicked without XML content
            if (!uploadedXmlContent) {
                const errorCode = logError("FILE_UPLOAD_ERROR");

                logAppInsightError("Next clicked without uploaded XML", buildUploadContext({
                    problemId: String(errorCode),
                    errorCode: String(errorCode),
                    function: "handleNext",
                    step,
                    method,
                    loanType,
                    hasUploadedFile: !!uploadedFile
                }));

                setErrorMessage(`Error Code: ${errorCode} - Please upload a MISMO XML file first.`);
                setShowErrorPopup(true);
                setSubmitting(false);
                return;
            }

            let loanObject = null;

            try {
                loanObject = await convertMismoToLoanObject(accessToken, uploadedXmlContent);
            } catch (error) {
                const errorCode = logError("MISMO_CONVERT_ERROR");

                logAppInsightError("MISMO convert failed (handleNext)", buildUploadContext({
                    problemId: String(errorCode),
                    errorCode: String(errorCode),
                    function: "handleNext",
                    rawError: error?.message || "convertMismoToLoanObject error",
                    responseData: error?.response?.data || null
                }));

                showToast(`❌ ${error.message}`, 8000);
                setSubmitting(false);
                return;
            }

            // 2. Consumer loan uploaded with Investor property
            if (
                loanType === "consumer" &&
                loanObject?.applications?.[0]?.propertyUsageType?.toLowerCase() === "investor"
            ) {
                const errorCode = logError("FILE_UPLOAD_ERROR");

                logAppInsightError("Consumer loan uploaded with investor XML", buildUploadContext({
                    problemId: String(errorCode),
                    errorCode: String(errorCode),
                    function: "handleNext",
                    propertyUsageType: loanObject.applications[0].propertyUsageType
                }));

                setErrorMessage("Please upload a consumer type MISMO XML file");
                setShowErrorPopup(true);
                setSubmitting(false);
                return;
            }

            // Extract and store property location
            state = loanObject?.property?.state?.trim() || "";
            const city = loanObject?.property?.city?.trim() || "";
            setPropertyState(state);
            setPropertyCity(city);
        }

        const tpoAuthObject = await window.elli.script.getObject("auth");
        tpoAuthObject.getUser().then(async function (user) {
            const authCode = await tpoAuthObject.createAuthCode();
            fetchExternalUserDetails(authCode, user.id, state);
        });

        setStep(4);
    };

    const handleNextManual = async () => {
        setSubmitting(true);
        const tpoAuthObject = await window.elli.script.getObject("auth");
        tpoAuthObject.getUser().then(async function (user) {
            const authCode = await tpoAuthObject.createAuthCode();
            fetchExternalUserDetails(authCode, user.id, "");
        });

        setStep(4);
    };

    const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file) {
            if (!file.name.toLowerCase().endsWith(".xml")) {
                const errorCode = logError("FILE_TYPE_INVALID"); // or use FILE_UPLOAD_ERROR if you don't want new code

                logAppInsightError("Invalid file type dropped", buildUploadContext({
                    problemId: String(errorCode),
                    errorCode: String(errorCode),
                    function: "onDrop",
                    attemptedFileName: file?.name || null,
                    attemptedFileType: file?.type || null,
                    attemptedFileSize: file?.size || null
                }));

                setFileError("🚨 Only MISMO 3.4 XML files are allowed to create a loan.");
                showToast(`🚨 Only MISMO 3.4 XML files are allowed to create a loan.`, 8000);
                setUploadedFile(null);
                setUploadedXmlContent("");
                return;
            }

            setFileError("");

            setUploadedFile(file);
            const reader = new FileReader();

            reader.onload = (e) => setUploadedXmlContent(e.target.result);

            reader.onerror = () => {
                const errorCode = logError("FILE_READ_ERROR"); // or FILE_UPLOAD_ERROR

                logAppInsightError("FileReader failed while reading XML", buildUploadContext({
                    problemId: String(errorCode),
                    errorCode: String(errorCode),
                    function: "onDrop",
                    uploadedFileName: file?.name || null,
                    uploadedFileType: file?.type || null,
                    uploadedFileSize: file?.size || null,
                    rawError: reader?.error?.message || "FileReader error"
                }));

                setFileError("🚨 Failed to read the XML file. Please try again.");
                showToast(`🚨 Only MISMO 3.4 XML files are allowed to create a loan.`, 8000);
                setUploadedFile(null);
                setUploadedXmlContent("");
            };

            reader.readAsText(file);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected: (fileRejections) => {
            const rej = fileRejections?.[0];
            const rejectedFile = rej?.file;

            const errorCode = logError("FILE_DROP_REJECTED");
            setFileError("🚨 Only MISMO 3.4 XML files are allowed to create a loan.");
            showToast(`🚨 Only MISMO 3.4 XML files are allowed to create a loan.`, 8000);
            setUploadedFile(null);
            setUploadedXmlContent("");

            logAppInsightError("Drop rejected (onDropRejected)", buildUploadContext({
                problemId: String(errorCode),
                errorCode: String(errorCode),
                rejectedFileName: rejectedFile?.name || null,
                rejectedFileType: rejectedFile?.type || null,
                rejectedFileSize: rejectedFile?.size || null,
                rejectionErrors: (rej?.errors || []).map(e => ({
                    code: e?.code,
                    message: e?.message
                }))
            }));
        },
        accept: { "text/xml": [".xml"], "application/xml": [".xml"] }, // better than ".xml"
        multiple: false
    });

    const resolveChannel = (channelType) => {
        switch (channelType) {
            case "Banked_Wholesale":
                return process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE;

            case "Correspondent_Delegate":
            case "Correspondent_NonDelegate":
                return process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT;

            default:
                return process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE;
        }
    };

    const applyChannelDecision = ({
        channelTypes,
        correspondentSelection,
        hasNmls,
        hasCreateLoan
    }) => {
        const decision = decideChannelFlow({
            channelTypes,
            correspondentSelection,
            hasNmls,
            hasCreateLoan
        });
        setChannelOptions(decision.channelOptions);
        setShowStep0(decision.requiresChannelSelection);

        if (decision.initialChannelType) {
            setChannelType(decision.initialChannelType);
        }

        if (decision.initialLoanType) {
            setLoanType(decision.initialLoanType);
            setForceBusinessFlow(true);
            setHideStep1(true);
            setHideBackInStep2(true);
        }

        setStep(decision.initialStep);
    };

    function decideChannelFlow({
        channelTypes = [],
        correspondentSelection = [],
        hasNmls,
        hasCreateLoan
    }) {
        const hasWholesale = channelTypes.includes("Broker");
        const hasCorrespondent = channelTypes.includes("Correspondent");
        const hasDelegate = correspondentSelection.includes("Delegated");
        const hasNonDelegate = correspondentSelection.includes("NonDelegated");

        // Build channel options
        const channelOptions = [
            hasWholesale && "Banked_Wholesale",
            hasCorrespondent && hasDelegate && hasCreateLoan && "Correspondent_Delegate",
            hasCorrespondent && hasNonDelegate && hasCreateLoan && "Correspondent_NonDelegate"
        ].filter(Boolean);

        const requiresChannelSelection = channelOptions.length > 1;
        const isBusinessForced = !hasNmls;

        // Decide initial step
        let initialStep;
        if (requiresChannelSelection) {
            initialStep = 0;
        } else if (isBusinessForced) {
            initialStep = 2; // skip step 1
        } else {
            initialStep = 1;
        }

        return {
            channelOptions,
            requiresChannelSelection,
            isBusinessForced,
            initialChannelType:
                channelOptions.length === 1 ? channelOptions[0] : "",
            initialLoanType: isBusinessForced ? "business" : null,
            hideStep1: isBusinessForced,
            hideBackInStep2: isBusinessForced,
            initialStep
        };
    }

    const fetchExternalUserDetails = async (authCode, TPOUserId, state = "") => {
        setTpoUserId(TPOUserId);
        try {
            info("ExternalUserDetails loaded", { component: "LoanForm", userid: TPOUserId });
            setTpoCompanies([]);
            setTpoLoanOfficers([]);
            setTpoLoanProcessors([]);
            setTpoParent(null);
            setStateLicenses(null);
            setSelectedCompany("");
            setSelectedLoanOfficer("");
            setSelectedLoanProcessor("");

            const response = await getTPOContact(authCode, TPOUserId, state);
            console.log('TPO Contact Response: ', response, channelOptions);
            if (response) {

                setTpoCompanies(response.companies || []);
                setTpoLoanOfficers(response.loanOfficers || []);
                setTpoLoanProcessors(response.loanProcessors || []);
                setTpoParent(response.parent || null);
                setStateLicenses(response.stateLicenses || null);

                if (response.companies && response.companies.length > 0) {
                    const company = response.companies[0];

                    if (channelType === 'Banked_Wholesale') {
                        setShowAEDropdown(company.showAEDropdown);
                    }
                    setSelectedCompany(company.id.toString());

                    // Determine AE source
                    let aeList = [];

                    if (company.salesRepAe && company.salesRepAe.length > 0) {
                        // Prefer company AE
                        aeList = company.salesRepAe;
                    } else if (response.parent?.salesRepAe && response.parent.salesRepAe.length > 0) {
                        // Fallback to parent AE
                        aeList = response.parent.salesRepAe;
                    }

                    // AE Filter based on Selection in External Company Settings.
                    switch (channelType) {
                        case "Correspondent_Delegate":
                            aeList = aeList.filter(x => x.isPrimarySalesRep || x.isDelegatedChannelEnabled);
                            break;
                        case "Correspondent_NonDelegate":
                            aeList = aeList.filter(x => x.isPrimarySalesRep || x.isNonDelegatedChannelEnabled);
                            break;
                        default:
                            aeList = aeList.filter(x => x.isPrimarySalesRep || x.isWholesaleChannelEnabled);
                    }

                    setSalesRepAeList(aeList);

                    // Auto-select if only one AE
                    if (showAEDropdown && aeList.length === 1) {
                        setSelectedAeUserId(aeList[0].userId);
                    }
                }
                setLoanOfficerList(
                    (response.loanOfficers || [])
                        .slice()
                        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                        .map(officer => ({
                            id: officer.id,
                            name: officer.name
                        }))
                );

                setLoanProcessorList(
                    (response.loanProcessors || [])
                        .slice()
                        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                        .map(processor => ({
                            id: processor.id,
                            name: processor.name
                        }))
                );

                // Cache the "main" org response (default behavior)
                setMainOrgCache(response);

                const mainCompany = (response.companies || [])[0];
                const children = mainCompany?.childOrganizations || [];

                const peer = (response.peerLoanAccess || response.PeerLoanAccess || "").trim();
                const mode = (response.accessMode || response.AccessMode || "").trim();

                setPeerLoanAccess(peer);

                const allowOrgSwitch = ENABLE_COMPANY_SWITCH && isReadWrite(peer) && isReadWrite(mode);

                const mainOrgEntityId = String(mainCompany?.orgId || "").trim(); // your "001"

                // Only show dropdown if children exist
                if (allowOrgSwitch && children.length > 0 && mainOrgEntityId) {
                    const TAB = "\u00A0\u00A0\u00A0\u00A0";
                    const options = [
                        {
                            value: mainOrgEntityId,
                            label: `${mainCompany.organizationName}`,   // parent line
                            isMain: true,
                            level: 0
                        },
                        ...children.map(ch => ({
                            value: String(ch.entityId),
                            label: `${TAB}↳ ${ch.entityName}`,
                            isMain: false,
                            level: 1
                        }))
                    ];

                    setOrgOptions(options);
                    setSelectedOrgEntityId(mainOrgEntityId); // default = parent
                } else {
                    setOrgOptions([]);
                    setSelectedOrgEntityId("");
                }

            }
        } catch (error) {
            console.error("Error fetching external user details:", error);
            const errorCode = logError("USER_FETCH_ERROR");
            setErrorMessage(`Error Code: ${errorCode} - fetching external user details.`);
            setShowErrorPopup(true);

            logAppInsightError(`${errorCode} - fetching external user details.`, {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - fetching external user details.`,
                component: "LoanForm"
            });
        } finally {
            setSubmitting(false);
        }
    };

    const refreshContactsByOrg = async (orgEntityId) => {
        if (!orgEntityId) return;

        setSwitchingOrg(true);
        try {
            // reset selections when org changes
            setSelectedLoanOfficer("");
            setSelectedLoanProcessor("");

            const selectedOpt = orgOptions.find(o => o.value === orgEntityId);

            // MAIN company selected -> restore cached LO/LP
            if (selectedOpt?.isMain && mainOrgCache) {
                const response = mainOrgCache;

                setTpoCompanies(response.companies || []);

                // IMPORTANT: keep selectedCompany in sync (so submit uses correct company)
                const company = (response.companies || [])[0];
                if (company?.id != null) setSelectedCompany(String(company.id));

                setTpoLoanOfficers(response.loanOfficers || []);
                setTpoLoanProcessors(response.loanProcessors || []);
                setTpoParent(response.parent || null);
                setStateLicenses(response.stateLicenses || null);

                setLoanOfficerList(
                    (response.loanOfficers || [])
                        .slice()
                        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                        .map(officer => ({ id: officer.id, name: officer.name }))
                );

                setLoanProcessorList(
                    (response.loanProcessors || [])
                        .slice()
                        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                        .map(processor => ({ id: processor.id, name: processor.name }))
                );

                return;
            }

            // CHILD selected -> call new endpoint
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();

            const resp = await getTPOContactByOrg(authCode, tpoUserId, propertyState, orgEntityId);

            setTpoCompanies(resp.companies || []);

            // IMPORTANT: keep selectedCompany in sync (so submit uses correct company)
            const company = (resp.companies || [])[0];
            if (company?.id != null) setSelectedCompany(String(company.id));

            setTpoLoanOfficers(resp.loanOfficers || []);
            setTpoLoanProcessors(resp.loanProcessors || []);
            setTpoParent(resp.parent || null);
            setStateLicenses(resp.stateLicenses || null);

            setLoanOfficerList(
                (resp.loanOfficers || [])
                    .slice()
                    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                    .map(officer => ({ id: officer.id, name: officer.name }))
            );

            setLoanProcessorList(
                (resp.loanProcessors || [])
                    .slice()
                    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                    .map(processor => ({ id: processor.id, name: processor.name }))
            );
        } catch (err) {
            const errorCode = logError("ORG_SWITCH_FAILED");

            logAppInsightError("Company / Branch switch failed", {
                problemId: errorCode,
                errorCode,
                component: "LoanForm",
                function: "refreshContactsByOrg",
                selectedOrgEntityId: orgEntityId,
                tpoUserId,
                propertyState,
                rawError: err?.message || String(err),
                responseData: err?.response?.data || null
            });

            console.error("Org switch failed:", err);

            showToast(
                `❌ Error Code: ${errorCode} - Failed to load contacts for selected company.`,
                8000
            );
        }
        finally {
            setSwitchingOrg(false);
        }
    };

    const handleSubmit = async () => {

        const selectedCompanyData = (tpoCompanies && tpoCompanies.length > 0)
            ? tpoCompanies[0]
            : null;
        const selectedLoanOfficerData = tpoLoanOfficers.find(lo => lo.id === selectedLoanOfficer);
        const selectedLoanProcessorData = tpoLoanProcessors.find(lp => lp.id === selectedLoanProcessor);
        //const corrUnderwriterData = corrUnderwriters.find(cu => cu.id === selectedCorrUnderwriter);
        const isCompanyRequired =
            ENABLE_COMPANY_SWITCH && orgOptions.length > 0;


        if (resolveChannel(channelType) === process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE) {

            if (
                (isCompanyRequired && !selectedOrgEntityId) ||
                !selectedLoanOfficerData ||
                !selectedLoanProcessorData
            ) {
                const errorCode = logError("INVALID_CONTACT_SELECTION");

                logAppInsightError("Invalid contact selection during loan submit", {
                    problemId: errorCode,
                    errorCode,
                    component: "LoanForm",
                    function: "handleSubmit",
                    peerLoanAccess,
                    selectedOrgEntityId,
                    selectedLoanOfficer,
                    selectedLoanProcessor
                });

                setErrorMessage(
                    `Error Code: ${errorCode} - Please select a valid ${isCompanyRequired ? "Company, " : ""
                    }Loan Officer and Loan Processor.`
                );
                setShowErrorPopup(true);
                setLoading(false);
                return;
            }
        }
        else {
            if (
                (isCompanyRequired && !selectedOrgEntityId) ||
                !selectedLoanOfficerData
            ) {
                const errorCode = logError("INVALID_CONTACT_SELECTION");

                logAppInsightError("Invalid contact selection during loan submit", {
                    problemId: errorCode,
                    errorCode,
                    component: "LoanForm",
                    function: "handleSubmit",
                    peerLoanAccess,
                    selectedOrgEntityId,
                    selectedLoanOfficer,
                    selectedLoanProcessor
                });

                setErrorMessage(
                    `Error Code: ${errorCode} - Please select a valid ${isCompanyRequired ? "Company, " : ""
                    }Correspondent Underwriter.`

                );
                setShowErrorPopup(true);
                setLoading(false);
                return;
            }
        }

        setSubmitting(true);

        let loDetails = null;
        let loLicense = null;
        let repOrgId = "";
        let repOrgName = "";
        let aeBdrInfoForLoan = null;

        try {
            loDetails = await getUserDetails(accessToken, selectedLoanOfficer);
            loLicense = getActiveLicenseForState(loDetails?.licenses, propertyState);

            const salesRepUserId = loDetails?.salesRep?.entityId;
            const nmlsId = loDetails?.nmlsId;
            if (salesRepUserId) {
                const tpoAuthObject = await window.elli.script.getObject("auth");
                const authCode = await tpoAuthObject.createAuthCode();
                const org = await getSalesRepAssignment(authCode, salesRepUserId, nmlsId);

                repOrgId = org?.orgId ? String(org.orgId).trim() : "";
                repOrgName = org?.orgName ? String(org.orgName).trim() : "";

                aeBdrInfoForLoan =
                    org?.AE_BDR_Info ||
                    org?.aE_BDR_Info ||
                    org?.ae_BDR_Info ||
                    null;

                setAeBdrInfo(aeBdrInfoForLoan);
            }


        } catch (e) {
            // AppInsights + stop submit
            const errorCode = logError("LO_ON_DEMAND_FETCH_ERROR");

            logAppInsightError("LO on-demand fetch failed", {
                problemId: errorCode,
                errorCode,
                component: "LoanForm",
                function: "handleSubmit",
                selectedLoanOfficer,
                propertyState,
                rawError: e?.message || String(e),
                responseData: e?.response?.data || null
            });

            setErrorMessage(`Error Code: ${errorCode} - Unable to validate Loan Officer (license/AE). Please try again.`);
            setShowErrorPopup(true);

            setSubmitting(false);
            setStep(4);
            return;
        }

        let divisionCustomerPk = "";
        const selectedLoanOfficerEmail = String(selectedLoanOfficerData?.email || loDetails?.email || "").trim();
        if (selectedLoanOfficerEmail) {
            try {
                const tpoAuthObject = await window.elli.script.getObject("auth");
                const brokerLoAeAuthCode = await tpoAuthObject.createAuthCode();
                const brokerLoAeAssignment = await getBrokerLoAeAssignment(brokerLoAeAuthCode, selectedLoanOfficerEmail);
                divisionCustomerPk = getTeamCustomerPk(
                    brokerLoAeAssignment?.team ?? brokerLoAeAssignment?.Team
                );
            } catch (assignmentError) {
                divisionCustomerPk = "";
                console.error("Broker LO AE assignment lookup failed:", assignmentError);
            }
        }

        setStep(5);

        info("Submitting loan", { loanType });

        const channel = resolveChannel(channelType);

        // Always treat the SELECTED company (parent OR branch) as the "main" company for loan creation
        const effectiveCompany = selectedCompanyData; // this is tpoCompanies[0] (updated by refreshContactsByOrg)
        const parentCompany = tpoParent || null;

        // Base TPO object
        let tpo = {
            registerDate: new Date().toISOString(),
            importSource: "MISMO 3.4",
            underwritingDelegated: false,
            preferredAus: "NoPreference",
            sITEID: selectedCompanyData?.tpocSetup?.tpoSiteDetails?.[0]?.siteId ?? null,
            ...extractCompanyDetails(effectiveCompany),
            ...(resolveChannel(channelType) === process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE
                ? {
                    ...extractUserDetails(selectedLoanOfficerData, "lo", selectedCompanyData),
                    ...extractUserDetails(selectedLoanProcessorData, "lp", selectedCompanyData)
                }
                : {
                    cfcName: selectedLoanOfficerData?.name || null,
                    cfcUserId: selectedLoanOfficerData?.id || null,
                    cfcEmail: selectedLoanOfficerData?.email || null,
                    cfcStatus: "Enabled",
                    cfcAddress: selectedLoanOfficerData?.address || null,
                    cfcCity: selectedLoanOfficerData?.city || null,
                    cfcState: selectedLoanOfficerData?.state || null,
                    cfcZip: selectedLoanOfficerData?.zip || null,
                }
            ),
        };

        // Add branch fields if parent exists
        if (parentCompany) {
            Object.assign(tpo, extractCompanyDetails(selectedCompanyData, "branch"));
        }

        const buildCommonLoanObject = () => {

            // Determine Parent Org NMLS ID
            const parentOrgNmlsId = tpoParent
                ? tpoParent?.businessInformation?.nmlsId || null
                : selectedCompanyData?.businessInformation?.nmlsId || null;

            let aeBranchId = "";
            let aeBranchName = "";

            if (showAEDropdown && Array.isArray(salesRepAeList) && salesRepAeList.length > 0) {
                const selectedAE = salesRepAeList.find(ae => ae.userId === selectedAeUserId);
                aeBranchId = selectedAE?.organizationId ? String(selectedAE.organizationId) : "";
                aeBranchName = selectedAE?.organizationName ? String(selectedAE.organizationName) : "";
            } else {
                aeBranchId = repOrgId ? String(repOrgId) : "";
                aeBranchName = repOrgName ? String(repOrgName) : "";
            }

            const common = {
                channel,
                tpo,
                customFields: [
                    {
                        fieldName: "CX.LOAN.TYPE",
                        value: loanType === "consumer" ? "Consumer" : "Business"
                    },
                    {
                        fieldName: "CX.PARENTORG.NMLS.ID",
                        value: parentOrgNmlsId
                    },
                    {
                        fieldName: "CX.AE.BRANCH.ID",
                        value: aeBranchId
                    },
                    {
                        fieldName: "CX.AE.BRANCH.NAME",
                        value: aeBranchName
                    },
                    {
                        fieldName: "CX.CMS.CUSTOMERPK",
                        value: divisionCustomerPk
                    }
                ],
                // Removing Loan Origator Information for Correspondent Loans
                ...(resolveChannel(channelType) === process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE
                    && {
                    interviewersId: tpo.loId,
                    interviewerEmail: selectedLoanOfficerData?.email ?? null,
                    interviewerName: buildInterviewerName(selectedLoanOfficerData),
                    interviewerPhoneNumber: selectedLoanOfficerData?.phone ?? null,
                    originatorFirstName: selectedLoanOfficerData?.firstName ?? null,
                    originatorLastName: selectedLoanOfficerData?.lastName ?? null,
                    nmlsLoanOriginatorId: selectedLoanOfficerData?.nmlsId ?? null,
                    originatorMiddleName: selectedLoanOfficerData?.middleName ?? null,
                    originatorSuffixName: selectedLoanOfficerData?.suffix ?? null,
                }
                ),
                loanSource: "Encompass TPO Connect",
                mortgageType: "Conventional"
            };

            // Business Purpose: set Application Date (loan.originationDate) = Today
            if ((loanType || "").trim().toLowerCase() === "business") {
                common.originationDate = getPhoenixISODate();
            }

            return common;
        };

        const runSoftLicenseWarnings = ({
            loanType,
            stateLicenses,
            selectedCompanyData,
            selectedLoanOfficerData,
            propertyState,
            propertyCity,
            loLicense,
            showToast
        }) => {
            try {
                const warnings = collectSoftLicenseWarnings(
                    loanType,
                    stateLicenses,
                    selectedCompanyData,
                    selectedLoanOfficerData,
                    propertyState,
                    propertyCity,
                    loLicense
                );

                if (!warnings || warnings.length === 0) return;

                // Normalize to array
                const rules = Array.isArray(stateLicenses)
                    ? stateLicenses
                    : (stateLicenses ? [stateLicenses] : []);

                const lt = (loanType || '').trim().toLowerCase();
                const effState = (propertyState || '').trim().toUpperCase();

                // Find rules for this state only (city ignored)
                const stateRules = rules.filter(r =>
                    ((r.stateCode || '').trim().toUpperCase() === effState)
                );

                const matchingRule = stateRules.find(r => {
                    const ruleLoanType = (r.loanType || '').trim().toLowerCase();
                    return ruleLoanType === lt || ruleLoanType === '';
                });

                const brokerEmpty =
                    matchingRule && (matchingRule.brokerLicenseRequired || '').trim() === '';
                const loEmpty =
                    matchingRule && (matchingRule.loanOriginatorLicenseRequired || '').trim() === '';

                if (matchingRule && brokerEmpty && loEmpty) {
                    const isBusiness = lt === 'business';

                    const msg =
                        `This ${isBusiness ? 'Business Purpose' : 'Consumer'} loan is not eligible for ` +
                        `${isBusiness ? 'investment' : 'Consumer'} transactions in ${propertyCity}, ${propertyState}. ` +
                        `You can create the loan, but you cannot submit it for this location.`;

                    showToast(msg, 8000);
                } else {
                    const html = `License warnings:<br>- ${warnings.join('<br>- ')}`;
                    showToast(html, 8000);
                }
            } catch (err) {
                console.error("Soft licensing check failed:", err);
            }
        };

        const createMISMOFile = async (accessToken, loanId) => {
            if (loanId && process.env.REACT_APP_LOAN_MISMO_FILE_NAME) {
                try {
                    const documentTitle = process.env.REACT_APP_LOAN_MISMO_FILE_FOLDER;
                    const fileName = process.env.REACT_APP_LOAN_MISMO_FILE_NAME;

                    // Step 1: Create a Document or reuse document container
                    let documentId = await getExistingDocumentId(accessToken, loanId, documentTitle);
                    if (!documentId) {
                        documentId = await createDocument(accessToken, loanId, documentTitle);
                    }

                    // Step 2: Get URL to Upload Attachment to eFolder
                    const uploadInfo = await getAttachmentUploadUrl(accessToken, loanId, fileName, uploadedXmlContent.length, "text/plain");

                    // Step 3: Upload Attachment
                    await uploadAttachmentBlob(uploadInfo.uploadUrl, uploadInfo.authorizationHeader, uploadedXmlContent, "text/plain");

                    // Step 4: Assign Attachments
                    await assignAttachmentToDocument(accessToken, loanId, documentId, uploadInfo.attachmentId);
                }
                catch (error) {
                    const message = error?.message || "Failed to create MISMO XML file for loan.";
                    console.error("MISMO File creation failed", error);
                    logAppInsightError(message, { error, location: "createMISMO", context: "Failed to create MISMO XML" });
                }
            }
        };

        const submitLoan = async (loanObject) => {
            try {
                if (channelType?.toLowerCase() === "correspondent_nondelegate") {
                    correspondentType = correspondentNonDelegated;
                }

                let loanId = createdLoanId;

                if (!loanId) {
                    const createdLoan = await createLoan(accessToken, loanObject, loanType, tpoUserId, channel, correspondentType);
                    if (!createdLoan?.id) {
                        throw new Error("Loan created response missing loan id.");
                    }

                    loanId = createdLoan.id;
                    const loanNumber = createdLoan.loanNumber || "";

                    setCreatedLoanId(loanId);
                    setCreatedLoanNumber(loanNumber);

                    sessionStorage.setItem("newLoan.createdLoanId", loanId);
                    sessionStorage.setItem("newLoan.createdLoanNumber", loanNumber);
                } else {
                    info(`Retrying post-create steps for existing LoanID: ${loanId}`, {
                        component: "LoanForm",
                        function: "submitLoan",
                        loanId
                    });
                }

                const fieldDictionary = {};

                if (propertyState && propertyState.trim() !== "") {
                    await updateLateFeeFields(accessToken, loanId, fieldDictionary);
                }

                const lienHolderFields = await getLienHolderFieldUpdates(accessToken, loanId);
                addFieldsToDictionary(fieldDictionary, lienHolderFields);

                let selectedAE = salesRepAeList.find(ae => ae.userId === selectedAeUserId);

                if (channelType?.toLowerCase() === "correspondent_nondelegate" || channelType?.toLowerCase() === "correspondent_delegate") {
                    selectedAE = await updateTPOAEtoLoan(salesRepAeList, loanId, fieldDictionary, selectedAeUserId);
                }

                if (
                    method === "import" &&
                    (channelType?.toLowerCase() === "correspondent_nondelegate" || channelType?.toLowerCase() === "correspondent_delegate")
                ) {
                    const lockContractPaths = correspondentLockedImportFields.map(x => x.contractPath);

                    await addFieldLockData(accessToken, loanId, lockContractPaths);

                    const importedLockedFields = correspondentLockedImportFields
                        .map(field => {
                            const value = getValueByContractPath(loanObject, field.contractPath);

                            return {
                                id: field.id,
                                value: value != null ? String(value) : ""
                            };
                        })
                        .filter(field => String(field.value || "").trim() !== "");

                    addFieldsToDictionary(fieldDictionary, importedLockedFields);
                }

                const finalFieldWriterPayload = buildFieldWriterPayload(fieldDictionary);

                // URLA.X81 is a lock field - createLoan does not set it, so write it (locked) here
                if (method === "import") {
                    finalFieldWriterPayload.push(...getImportedRentalIncomeFieldWrite(loanObject));
                }

                if (finalFieldWriterPayload.length > 0) {
                    await fieldWriter(accessToken, loanId, finalFieldWriterPayload);

                    info(`Post-create fields updated for LoanID: ${loanId}`, {
                        component: "LoanForm",
                        function: "submitLoan",
                        loanId,
                        fields: finalFieldWriterPayload.map(x => x.id)
                    });
                }

                const normalizedChannelType = String(channelType || "").trim().toLowerCase();

                const isWholesaleFlow =
                    normalizedChannelType === "banked_wholesale";

                const isCorrespondentFlow =
                    normalizedChannelType === "correspondent_delegate" ||
                    normalizedChannelType === "correspondent_nondelegate";

                const shouldUpdateAeBdr =
                    ENABLE_AE_BDR_FEATURE &&
                    (
                        isWholesaleFlow ||
                        (isCorrespondentFlow && ENABLE_AE_BDR_FOR_CORR)
                    );

                await applyAEtoLoan(
                    accessToken,
                    loanId,
                    selectedAE,
                    aeBdrInfoForLoan || aeBdrInfo,
                    shouldUpdateAeBdr
                );

                if (method === "import" && uploadedXmlContent) {
                    await createMISMOFile(accessToken, loanId);
                }

                sessionStorage.removeItem("newLoan.createdLoanId");
                sessionStorage.removeItem("newLoan.createdLoanNumber");
                setCreatedLoanId("");
                setCreatedLoanNumber("");

                redirectToLoanPage(loanId);
            } catch (error) {
                setStep(4);
                handleLoanError(error);
            } finally {
                setSubmitting(false);
            }
        };

        if (method === "import") {
            if (!uploadedXmlContent) {
                const errorCode = logError("FILE_UPLOAD_ERROR");
                setErrorMessage(`Error Code: ${errorCode} - Please upload a MISMO XML file first.`);
                setShowErrorPopup(true);
                setSubmitting(false);
                return;
            }

            try {
                let xmlContentForLoanConvert = uploadedXmlContent;
                if (channel === process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE) {
                    try {
                        const parser = new DOMParser();
                        const xmlDoc = parser.parseFromString(uploadedXmlContent, "text/xml");

                        // Wholesale: ignore all service metadata from MISMO import.
                        const serviceNodes = xmlDoc.querySelectorAll("SERVICES");
                        serviceNodes.forEach((node) => {
                            if (node.parentNode) {
                                node.parentNode.removeChild(node);
                            }
                        });

                        xmlContentForLoanConvert = new XMLSerializer().serializeToString(xmlDoc);
                    } catch (e) {
                        console.warn("Wholesale XML sanitize failed; proceeding with original XML.", e);
                    }
                }

                const loanObject = await convertMismoToLoanObject(accessToken, xmlContentForLoanConvert);
                if (channel === process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT) {
                    const propertyEstimatedValue = (() => {
                        try {
                            const parser = new DOMParser();
                            const xmlDoc = parser.parseFromString(uploadedXmlContent, "text/xml");
                            // MISMO 3.4: <PROPERTY_VALUATION_DETAIL><PropertyValuationAmount>
                            const node = xmlDoc.querySelector("PROPERTY_VALUATION_DETAIL > PropertyValuationAmount");
                            return node ? node.textContent : null;
                        } catch (e) {
                            return null;
                        }
                    })();
                    const propertyType = (() => {
                        try {
                            const parser = new DOMParser();
                            const xmlDoc = parser.parseFromString(uploadedXmlContent, "text/xml");
                            // MISMO 3.4: prefer <PROPERTY_DETAIL><GSEPropertyType>, fallback to <PropertyType> or <PropertyStructureBuiltType>
                            const node =
                                xmlDoc.querySelector("PROPERTY_DETAIL > GSEPropertyType") ||
                                xmlDoc.querySelector("PROPERTY_DETAIL > PropertyType") ||
                                xmlDoc.querySelector("PROPERTY_DETAIL > PropertyStructureBuiltType");
                            return node ? node.textContent : null;
                        } catch (e) {
                            return null;
                        }
                    })();
                    // Convert to integer if possible, otherwise null
                    const propertyEstimatedValueInt = propertyEstimatedValue ? parseInt(propertyEstimatedValue, 10) : null;
                    Object.assign(loanObject, { propertyEstimatedValueAmount: propertyEstimatedValueInt });
                    const propertyTypeTrimmed = propertyType ? propertyType.trim() : "";
                    Object.assign(loanObject, { loanProductData: { ...loanObject.loanProductData, gsePropertyType: propertyTypeTrimmed } });
                    console.log("Converted loan object test:", loanObject);
                }
                if (loanObject) {
                    Object.assign(loanObject, buildCommonLoanObject());

                    //Update loan channel subtype for correspondent delegate/non-delegate once loan is created
                    if (channel === process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT) {
                        loanObject.tpo.underwritingDelegated = channelType === "Correspondent_Delegate" ? true : false;
                    } else {
                        // Remove MERS Number if available
                        loanObject.mersNumber = "";
                    }

                    // Update companyId in BROKER_LENDER contact if it exists
                    const brokerLenderContact = loanObject.contacts?.find(c => c.contactType === "BROKER_LENDER");

                    if (brokerLenderContact && selectedCompanyData?.businessInformation?.nmlsId) {
                        brokerLenderContact.companyId = selectedCompanyData.businessInformation.nmlsId;
                    }

                    if (loanObject.loanAmortizationType === "AdjustableRate") {
                        loanObject.loanProductData = loanObject.loanProductData ?? {};
                        const armIndexType = "SOFR_30DayAvg";
                        loanObject.loanProductData.armDisclosureType = armIndexType;
                        try {
                            const indexValue = await getArmIndex(accessToken, armIndexType);
                            if (indexValue != null) {
                                loanObject.loanProductData.indexCurrentValuePercent = indexValue;
                                loanObject.loanProductData.indexDate = getPhoenixISODate();
                            }
                        } catch (err) {
                            console.error("ARM index lookup failed:", err);
                        }
                    }

                    // Set company state license if available
                    const companyLicense = selectedCompanyData?.license;
                    if (companyLicense?.licenseNumber) {
                        loanObject.interviewersCompanyStateLicense = companyLicense.licenseNumber;
                    }

                    // Set loan officer license if available
                    const officerLicense = loLicense;
                    if (officerLicense?.licenseNumber) {
                        loanObject.interviewerLicenseIdentifier = officerLicense.licenseNumber;
                    } else {
                        loanObject.interviewerLicenseIdentifier = "";
                    }

                    if (loanType === "business" && Array.isArray(loanObject.applications) && loanObject.applications.length > 0) {
                        loanObject.applications[0].propertyUsageType = "Investor";
                    }

                    if (!skipStateLicenseValidation(selectedCompanyData.companyLicense.lenderType)) {
                        if (channel === process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE) {
                            runSoftLicenseWarnings({
                                loanType,
                                stateLicenses,
                                selectedCompanyData,
                                //selectedLoanOfficerData: resolveChannel(channelType) === process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE ? selectedLoanOfficerData : corrUnderwriterData,
                                selectedLoanOfficerData,
                                propertyState,
                                propertyCity,
                                loLicense,
                                showToast
                            });
                        }
                    }

                    // SkyTPO: Estimated Net Monthly Rental Income (URLA.X81) is only kept when the file has REO properties
                    clearRentalIncomeWithoutReo(loanObject);

                    addLoCompensationIfAvailable(loanObject, selectedCompanyData, selectedLoanOfficer);
                    await submitLoan(loanObject);
                }
            } catch (error) {
                setStep(4);
                logAppInsightError("MISMO convert failed (handleSubmit)", {
                    problemId: logError("MISMO_CONVERT_ERROR"),
                    errorCode: "MISMO_CONVERT_ERROR",
                    component: "LoanForm",
                    function: "handleSubmit",
                    step,
                    method,
                    loanType,
                    rawError: error?.message,
                    responseData: error?.response?.data || null,
                    hasUploadedFile: !!uploadedFile,
                    uploadedFileName: uploadedFile?.name || null,
                    hasXmlContent: !!uploadedXmlContent
                });

                handleLoanError(error);
                setSubmitting(false);
            }
        } else if (method === "manual") {
            const loanObject = buildCommonLoanObject();
            //Update loan channel subtype for correspondent delegate/non-delegate once loan is created
            if (channel === process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT) {
                loanObject.tpo.underwritingDelegated = channelType === "Correspondent_Delegate" ? true : false;
            }

            applyStaticImportParityForManual(loanObject, {
                loanType,
                selectedCompanyData,
                loLicense
            });

            addLoCompensationIfAvailable(loanObject, selectedCompanyData, selectedLoanOfficer);
            await submitLoan(loanObject);
        }
    };

    const handleBackAfterError = () => {
        if (createdLoanId) {
            const displayLoanNumber =
                createdLoanNumber ||
                sessionStorage.getItem("newLoan.createdLoanNumber") ||
                createdLoanId;


            setErrorMessage(
                `Loan has already been created. Loan Number: ${displayLoanNumber}. Please click Submit again to retry remaining updates, or open the loan.`
            );
            setShowErrorPopup(true);
            return;
        }

        setStep(method === "import" ? 3 : 2);
    };

    const redirectToLoanPage = async (loanId) => {
        const pageName = "Loan Summary";

        if (window.elli && window.elli.script) {
            window.elli.script.getObject("tpoApplication").then(async (tpoAppObject) => {
                const section = pageName.startsWith("URLA:") ? pageName.split(":")[1].trim() : null;
                await tpoAppObject.pageRedirect(section ? "urla" : pageName, { id: loanId, section });
            });
        } else {
            console.warn("elli.script is not available for redirection.");
        }
    };

    const handleLoanError = (error) => {
        const errorCode = logError("LOAN_PROCESS_ERROR");
        const apiData = error?.response?.data;

        const { message, parsedErrors } = formatLoanErrorMessage({
            errorCode,
            apiData,
            error
        });

        const savedLoanId =
            createdLoanId ||
            sessionStorage.getItem("newLoan.createdLoanId") ||
            "";

        const displayLoanNumber =
            createdLoanNumber ||
            sessionStorage.getItem("newLoan.createdLoanNumber") ||
            savedLoanId;

        const finalMessage = savedLoanId
            ? `Loan has already been created. Loan Number: ${displayLoanNumber}. Please click Submit again to retry remaining updates, or open the loan.\n\n${message}`
            : message;

        setErrorMessage(finalMessage);
        setShowErrorPopup(true);

        logAppInsightError("Loan processing failed", {
            problemId: errorCode,
            errorCode,
            errorMessage: finalMessage,
            parsedErrors,
            component: "LoanForm",
            createdLoanId: savedLoanId,
            createdLoanNumber: displayLoanNumber
        });
    };

    const LoanTypeBadge = () => {
        if (!loanType) return null;

        const handleClick = () => {
            // If forced business flow, never go to step 1
            if (forceBusinessFlow) {
                setStep(2);
                return;
            }
            // Move to Step 1 unconditionally
            setStep(1);

            // Reset fields to initial state
            setUploadedFile(null);
            setUploadedXmlContent("");
            setFileError("");
        };

        return (
            <div
                className="loan-type-badge"
                onClick={handleClick}
                style={{ top: '1rem', marginBottom: '2rem' }}
            >
                <span>{loanType === "consumer" ? "🧍 Consumer Loan" : "🏢 Business Loan"}</span>
            </div>
        );
    };

    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastAnimation, setToastAnimation] = useState('animate-fade-in-right');

    const showToast = (htmlMessage, timeoutMs = 5000) => {
        setToastMessage(htmlMessage);
        setToastVisible(true);
        setToastAnimation('animate-fade-in-right');
        // auto-hide
        setTimeout(() => {
            setToastAnimation('animate-fade-out-right');
            setTimeout(() => setToastVisible(false), 500);
        }, timeoutMs);
    };

    // Permission gate: hide everything when user lacks CREATE_LOANS access.
    // Evaluated as soon as personas resolve, independent of data loading.
    if (hasCreateLoanAccess === false) {
        return (
            <div className="flex items-center justify-center min-h-screen p-6">
                <div className="text-center text-gray-700 max-w-md">
                    <div className="text-lg font-semibold mb-2">Access Restricted</div>
                    <div className="text-sm">
                        You don’t have permission to create new loans. If you believe
                        this is a mistake, please reach out to your System
                        Administrator for access.
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return <Loading />;
    }

    const channelOptionMap = {
        Banked_Wholesale: "Wholesale",
        Correspondent_Delegate: "Correspondent Delegated",
        Correspondent_NonDelegate: "Correspondent Non-Delegated"
    };

    return (
        <>
            {submitting && <FormLoader />}
            {switchingOrg && <FormLoader />}
            <Toast
                visible={toastVisible}
                type="warning"
                message={toastMessage}
                onClose={() => setToastVisible(false)}
                animationClass={toastAnimation}
            />

            {step === 0 && showStep0 && (
                <div className="loan-container-channel relative">
                    <div className="max-w-4xl mx-auto mt-12">
                        <h2 className="loan-heading text-center">Select Channel</h2>

                        <div
                            className={`grid gap-6 mt-8 justify-items-stretch
                            ${channelOptions.length === 2 ? "grid-cols-1 md:grid-cols-2" : ""}
                            ${channelOptions.length === 3 ? "grid-cols-1 md:grid-cols-3" : ""}
                        `}
                        >

                            {channelOptions.map(optionId => (
                                <div
                                    key={optionId}
                                    onClick={() => setChannelType(optionId)}
                                    className={`border rounded-lg p-6 cursor-pointer transition-all
                                    ${channelType === optionId
                                            ? "border-blue-600 shadow-md ring-2 ring-blue-500"
                                            : "border-gray-300 hover:border-blue-400"}
                                    `}
                                >
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="radio"
                                            checked={channelType === optionId}
                                            className="pointer-events-none"
                                        />
                                        <span className="font-semibold">
                                            {channelOptionMap[optionId]}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>


                        <div className="flex justify-end gap-4 mt-10">
                            <button
                                className="submit-button"
                                disabled={!channelType}
                                onClick={() => {
                                    if (forceBusinessFlow) {
                                        setStep(2);
                                    } else {
                                        setStep(1);
                                    }
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {step !== 0 && (<div className="loan-container relative">
                {/* Step 1 */}
                {step === 1 && !hideStep1 && (
                    <>
                        <h2 className="loan-heading">What is the purpose of this loan?</h2>
                        <p className="loan-subheading">Help us understand whether it's for business or personal use.</p>

                        <div className="loan-options">
                            <button
                                className={`loan-button consumer ${loanType === "consumer" ? "selected" : ""}`}
                                onClick={() => {
                                    setLoanType("consumer");
                                    setStep(2); // Immediately go to next step
                                }}
                            >
                                <div className="flex flex-col items-center">
                                    🧍 Consumer
                                    <span className="block text-xs">(OO/2nd Home)</span>
                                </div>
                            </button>
                            <button
                                className={`loan-button business ${loanType === "business" ? "selected" : ""}`}
                                onClick={() => {
                                    setLoanType("business");
                                    setStep(2); // Immediately go to next step
                                }}
                            >
                                <div className="flex flex-col items-center">
                                    🏢 Business
                                    <span className="block text-xs">(NOO/Investment)</span>
                                </div>
                            </button>
                        </div>
                        {showStep0 && (
                            <button
                                className="back-button"
                                onClick={() => setStep(0)}
                            >
                                ← Back
                            </button>
                        )}

                    </>
                )}

                {/* STEP 2: Method */}
                {step === 2 && (
                    <>
                        <LoanTypeBadge />
                        <h2 className="loan-heading">How would you like to enter loan data?</h2>

                        <p className="loan-subheading">You can either import a file or fill it out manually.</p>

                        <div className="loan-options">
                            <button
                                className={`loan-button import ${method === "import" ? "selected" : ""}`}
                                onClick={() => {
                                    setMethod("import");
                                    setStep(3);
                                }}
                            >
                                📁 Import File
                            </button>
                            <button
                                className={`loan-button manual ${method === "manual" ? "selected" : ""}`}
                                onClick={() => {
                                    setMethod("manual");
                                    handleNextManual();
                                }}
                            >
                                📝 Manual Entry
                            </button>
                        </div>

                        {!hideBackInStep2 && (
                            <button className="back-button" onClick={() => setStep(1)}>← Back</button>
                        )}
                        {showStep0 && hideBackInStep2 && (
                            <button
                                className="back-button"
                                onClick={() => setStep(0)}
                            >
                                ← Back
                            </button>
                        )}

                    </>
                )}


                {/* STEP 3: File Upload */}
                {step === 3 && (
                    <>
                        <LoanTypeBadge />
                        <h2 className="loan-heading">Upload Your Loan File</h2>
                        <p className="loan-subheading">Accepted formats: .xml</p>

                        <div {...getRootProps()} className="upload-box">
                            <input {...getInputProps()} />
                            {isDragActive ? (
                                <p className="text-blue-600">Drop the file here...</p>
                            ) : (
                                <p className="text-gray-600"><span className="upload-icon">☁️</span> Drag & drop or click to browse</p>
                            )}
                            {uploadedFile && <p className="text-green-600 text-sm mt-2">Uploaded: {uploadedFile.name}</p>}
                            {fileError && <p className="text-red-600 text-sm mt-2">{fileError}</p>}
                        </div>

                        <p className="text-xs text-red-500 mt-2">Only MISMO 3.4-compliant XML files are supported for loan creation.</p>

                        <div className="flex justify-between mt-8">
                            <button className="back-button" onClick={() => setStep(2)}>← Back</button>
                            <button className="submit-button" disabled={!uploadedFile} onClick={handleNext}>Next →</button>
                        </div>

                    </>
                )}

                {/* Step 4 */}
                {(step === 4 || step === 5) && (
                    <>
                        <LoanTypeBadge />
                        <h2 className="loan-heading">Select Contacts</h2>
                        {(resolveChannel(channelType) === process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE) && (
                            <div className="mt-6">
                                {ENABLE_COMPANY_SWITCH && orgOptions.length > 0 && (
                                    <div className="mb-5">
                                        <h3 className="text-lg font-semibold text-blue-900 mb-2" style={{ color: 'var(--blue-accent)' }}>
                                            Company
                                        </h3>

                                        <select
                                            value={selectedOrgEntityId}
                                            onChange={async (e) => {
                                                const val = e.target.value;
                                                setSelectedOrgEntityId(val);
                                                await refreshContactsByOrg(val); // parent restores cached; child calls API
                                            }}
                                            className="custom-select w-full border border-gray-300 rounded-md p-2 text-xs"
                                        >
                                            {orgOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {showAEDropdown && salesRepAeList.length > 0 && (
                                    <div className="mt-6">
                                        <h3 className="text-lg font-semibold text-blue-900 mb-2" style={{ color: 'var(--blue-accent)' }}>Account Executive</h3>
                                        <div className="space-y-6">
                                            <div>
                                                <select
                                                    value={selectedAeUserId}
                                                    onChange={(e) => setSelectedAeUserId(e.target.value)}
                                                    className="custom-select w-full border border-gray-300 rounded-md p-2 mb-5 text-xs"
                                                >
                                                    <option value="">Select</option>
                                                    {salesRepAeList.map((ae) => (
                                                        <option key={ae.userId} value={ae.userId}>
                                                            {ae.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <h3 className="text-lg font-semibold text-blue-900 mb-2" style={{ color: 'var(--blue-accent)' }}>Loan Officer</h3>

                                <div className="space-y-6">
                                    <div>
                                        <select
                                            value={selectedLoanOfficer}
                                            onChange={(e) => setSelectedLoanOfficer(e.target.value)}
                                            className="custom-select w-full border border-gray-300 rounded-md p-2 text-xs"
                                        >
                                            <option value="">Select</option>
                                            {loanOfficerList.map((officer) => (
                                                <option key={officer.id} value={officer.id}>
                                                    {officer.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <h3 className="text-lg font-semibold text-blue-900 mb-2 mt-5" style={{ color: 'var(--blue-accent)' }}>Loan Processor</h3>
                                <div className="space-y-6">
                                    <div>
                                        <select
                                            value={selectedLoanProcessor}
                                            onChange={(e) => setSelectedLoanProcessor(e.target.value)}
                                            className="custom-select w-full border border-gray-300 rounded-md p-2 text-xs"
                                        >
                                            <option value="">Select</option>
                                            {loanProcessorList.map((processor) => (
                                                <option key={processor.id} value={processor.id}>
                                                    {processor.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="flex justify-between mt-8">
                                    <button className="back-button" onClick={handleBackAfterError}>← Back</button>
                                    <button
                                        className="submit-button"
                                        disabled={
                                            !selectedLoanOfficer ||
                                            !selectedLoanProcessor ||
                                            (showAEDropdown && salesRepAeList.length > 0 && !selectedAeUserId)
                                        }
                                        onClick={handleSubmit}
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        )}
                        {(resolveChannel(channelType) !== process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE) && (
                            <div className="mt-6">
                                {ENABLE_COMPANY_SWITCH && orgOptions.length > 0 && (
                                    <div className="mb-5">
                                        <h3 className="text-lg font-semibold text-blue-900 mb-2" style={{ color: 'var(--blue-accent)' }}>
                                            Company
                                        </h3>

                                        <select
                                            value={selectedOrgEntityId}
                                            onChange={async (e) => {
                                                const val = e.target.value;
                                                setSelectedOrgEntityId(val);
                                                await refreshContactsByOrg(val); // parent restores cached; child calls API
                                            }}
                                            className="custom-select w-full border border-gray-300 rounded-md p-2 text-xs"
                                        >
                                            {orgOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {showAEDropdown && salesRepAeList.length > 0 && (
                                    <div className="mt-6">
                                        <h3 className="text-lg font-semibold text-blue-900 mb-2" style={{ color: 'var(--blue-accent)' }}>Account Executive</h3>
                                        <div className="space-y-6">
                                            <div>
                                                <select
                                                    value={selectedAeUserId}
                                                    onChange={(e) => setSelectedAeUserId(e.target.value)}
                                                    className="custom-select w-full border border-gray-300 rounded-md p-2 mb-5 text-xs"
                                                >
                                                    <option value="">Select</option>
                                                    {salesRepAeList.map((ae) => (
                                                        <option key={ae.userId} value={ae.userId}>
                                                            {ae.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <h3 className="text-lg font-semibold text-blue-900 mb-2" style={{ color: 'var(--blue-accent)' }}>File Contacts</h3>

                                <div className="space-y-6">
                                    <div>
                                        <select
                                            value={selectedLoanOfficer}
                                            onChange={(e) => setSelectedLoanOfficer(e.target.value)}
                                            className="custom-select w-full border border-gray-300 rounded-md p-2 text-xs"
                                        >
                                            <option value="">Select</option>
                                            {loanOfficerList.map((officer) => (
                                                <option key={officer.id} value={officer.id}>
                                                    {officer.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>


                                <div className="flex justify-between mt-8">
                                    <button className="back-button" onClick={handleBackAfterError}>← Back</button>
                                    <button
                                        className="submit-button"
                                        disabled={
                                            !selectedLoanOfficer ||
                                            (showAEDropdown && salesRepAeList.length > 0 && !selectedAeUserId)
                                        }
                                        onClick={handleSubmit}
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className="progress-container mt-5">
                    <div className="progress-bar" style={{ width: `${getProgressWidthChannels(step)}%` }} />
                </div>

                {showErrorPopup && (() => {
                    const parsed = parseErrorModalBlocks(errorMessage);
                    if (!parsed) return null;

                    const { heading, general, remaining } = parsed;

                    return (
                        <div className="app-modal-overlay">
                            <div className="app-modal" role="dialog" aria-modal="true" aria-labelledby="app-modal-title">
                                <div className="app-modal-header">
                                    <div id="app-modal-title" className="app-modal-title">Error</div>
                                    <button
                                        className="app-modal-x"
                                        aria-label="Close"
                                        onClick={() => setShowErrorPopup(false)}
                                    >
                                        ✕
                                    </button>
                                </div>

                                <div className="app-modal-body">
                                    {/* Heading always its own block */}
                                    {heading ? (
                                        <div className="app-modal-heading" style={{ textAlign: "left", fontWeight: 500 }}>
                                            {heading}
                                        </div>
                                    ) : null}

                                    {/* General details on its own line */}
                                    {general ? (
                                        <div className="app-modal-general" style={{ textAlign: "left", marginTop: "8px" }}>
                                            {general}
                                        </div>
                                    ) : null}

                                    {/* Detailed error blocks */}
                                    {remaining.length > 0 && (
                                        <ErrorBlocks remaining={remaining} />
                                    )}
                                </div>

                                <div className="app-modal-footer">
                                    <button
                                        className="submit-button"
                                        onClick={() => setShowErrorPopup(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

            </div>
            )}
        </>
    );
};

export default NewLoanForm;